import { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, PhoneOff, Square, Loader2, Volume2, Info, Send } from 'lucide-react';
import { VoiceAdapter } from '../utils/voiceAdapter.js'; // availabilityReason only
import { useCompanion } from '../utils/companionStore.js';
import { useT } from '../utils/i18n';

// The Talk bar. Native GPT-Live conversation: Live listens, talks back in its
// own voice (audible), and delegates collection questions to the backend. The
// bar shows what the mic hears, what Live is saying, and every failure that
// needs the user: nothing waits silently.
export default function VoiceBar({ context }) {
  const { t } = useT();
  const { capabilities, stopSpeech, markUserInput, noteSpoken, voice } = useCompanion();
  const [state, setState] = useState('idle');
  const [reason, setReason] = useState(null);
  const [heard, setHeard] = useState('');        // transient: unanswered words the mic is hearing
  const [saying, setSaying] = useState('');      // transient: Live's current spoken wording
  const [elapsed, setElapsed] = useState(0);
  const [usage, setUsage] = useState(null);
  const [note, setNote] = useState(null);        // { kind: 'empty' | 'error' | 'unanswered', text, pending }
  const [ackMissing, setAckMissing] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const adapterRef = useRef(null);

  const unavailableReason = useMemo(() => VoiceAdapter.availabilityReason(capabilities), [capabilities]);

  useEffect(() => {
    // The app-wide shared session (CompanionProvider): the scanner may already
    // be holding it, and it keeps running after this sheet closes.
    const a = voice.adapter;
    adapterRef.current = a;
    setState(a.state); setReason(a.reason);
    const onState = (e) => { setState(e.detail.state); setReason(e.detail.reason); if (e.detail.state === 'connecting') { setNote(null); setAckMissing(false); setSaying(''); } };
    // Only genuinely new words are user input: they stop any local playback of
    // a text answer and mark newer input. Revisions, empty frames and the
    // bookkeeping emit after a window is consumed do neither. Backend work is
    // never cancelled by speech.
    const onUser = (e) => { setHeard(e.detail.text || ''); if (e.detail.newSpeech) { markUserInput(); stopSpeech('barge-in'); setNote(null); } };
    const onSaid = (e) => { setSaying(e.detail.final ? '' : e.detail.text); noteSpoken?.(e.detail); };
    const onClosed = (e) => setUsage(e.detail.usage || null);
    const onDelegation = () => { setNote(null); };
    // The caption follows the adapter's pending words (its consume/answered
    // events update it); a result must not blank words spoken meanwhile.
    const onResult = () => { setHeard(a.pendingSpeech()); };
    const onEmpty = (e) => setNote({ kind: 'empty', pending: e.detail.pending || '' });
    const onError = (e) => setNote({ kind: 'error', text: e.detail.message || null });
    const onUnanswered = (e) => setNote({ kind: 'unanswered', pending: e.detail.text || '' });
    const onAckMissing = () => setAckMissing(true);
    const onAudio = (e) => setAudioBlocked(!!e.detail.blocked);
    a.addEventListener('state', onState); a.addEventListener('user-speech', onUser); a.addEventListener('assistant-speech', onSaid);
    a.addEventListener('closed', onClosed); a.addEventListener('delegation', onDelegation); a.addEventListener('delegation-result', onResult);
    a.addEventListener('delegation-empty', onEmpty); a.addEventListener('delegation-error', onError); a.addEventListener('unanswered-speech', onUnanswered);
    a.addEventListener('ack-missing', onAckMissing); a.addEventListener('audio', onAudio);
    const timer = setInterval(() => setElapsed(a.elapsedSeconds()), 1000);
    const onHide = () => { if (document.hidden && a.pc) a.stop(t('companion.voice.stoppedHidden')); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      clearInterval(timer); document.removeEventListener('visibilitychange', onHide);
      a.removeEventListener('state', onState); a.removeEventListener('user-speech', onUser); a.removeEventListener('assistant-speech', onSaid);
      a.removeEventListener('closed', onClosed); a.removeEventListener('delegation', onDelegation); a.removeEventListener('delegation-result', onResult);
      a.removeEventListener('delegation-empty', onEmpty); a.removeEventListener('delegation-error', onError); a.removeEventListener('unanswered-speech', onUnanswered);
      a.removeEventListener('ack-missing', onAckMissing); a.removeEventListener('audio', onAudio);
      voice.release('sheet'); // ends the session only if nobody else (the scanner) holds it
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (adapterRef.current && voice.holders.has('sheet')) adapterRef.current.context = context || {}; }, [context, voice.holders]);

  const live = ['connecting', 'listening', 'speaking', 'delegating', 'closing'].includes(state);
  const start = () => { setUsage(null); setHeard(''); setSaying(''); setNote(null); voice.acquire('sheet', { context }); };
  const end = () => { stopSpeech('ended'); voice.end(); };
  const cancel = () => adapterRef.current?.cancelDelegation();
  const sendPending = () => { setNote(null); adapterRef.current?.sendPending(); };
  const unblock = () => adapterRef.current?.playAudio();
  const mm = Math.floor(elapsed / 60), ss = String(elapsed % 60).padStart(2, '0');

  if (unavailableReason && !live) {
    return (
      <div className="cmp-voice cmp-voice-off">
        <button type="button" className="cmp-talk" disabled title={unavailableReason}><Mic size={16} /> {t('companion.voice.talk')}</button>
        <div className="cmp-asof">{unavailableReason}</div>
      </div>
    );
  }

  const canSend = live && !!(note?.pending || heard);

  return (
    <div className={`cmp-voice cmp-voice-${state}`}>
      {!live && (
        <div className="cmp-voice-row">
          <button type="button" className="cmp-talk" onClick={start}><Mic size={16} /> {t('companion.voice.talk')}</button>
          <span className="cmp-ai-note"><Info size={11} /> {t('companion.voice.aiNote')}</span>
        </div>
      )}
      {live && (
        <div className="cmp-voice-live">
          <span className={`cmp-voice-dot ${state}`} aria-hidden="true" />
          <span className="cmp-voice-state">
            {state === 'connecting' && <><Loader2 size={14} className="cmp-spin" /> {t('companion.voice.connecting')}</>}
            {state === 'listening' && (saying ? t('companion.voice.speaking') : t('companion.voice.listening'))}
            {state === 'delegating' && <><Loader2 size={14} className="cmp-spin" /> {t('companion.voice.working')}</>}
            {state === 'closing' && <><Loader2 size={14} className="cmp-spin" /> {t('companion.voice.closing')}</>}
          </span>
          <span className="cmp-asof">{mm}:{ss}</span>
          {state === 'delegating' && <button type="button" className="btn btn-secondary cmp-btn-sm" onClick={cancel} title={t('companion.voice.cancelHint')}><Square size={12} /> {t('companion.voice.cancelRequest')}</button>}
          <button type="button" className="btn btn-danger cmp-btn-sm" onClick={end} disabled={state === 'closing'}><PhoneOff size={12} /> {t('companion.voice.end')}</button>
        </div>
      )}
      {live && heard && <div className="cmp-caption heard" aria-live="polite"><span className="cmp-caption-who">{t('companion.voice.you')}</span> {heard}</div>}
      {live && saying && <div className="cmp-caption answer" aria-live="polite"><span className="cmp-caption-who">{t('companion.voice.bindarr')}</span> {saying}</div>}
      {live && audioBlocked && (
        <button type="button" className="btn btn-secondary cmp-btn-sm" onClick={unblock}><Volume2 size={13} /> {t('companion.voice.tapToHear')}</button>
      )}
      {live && note && (
        <div className={`cmp-note cmp-inline ${note.kind === 'error' ? 'cmp-note-error' : ''}`} data-testid={`voice-note-${note.kind}`}>
          <span>
            {note.kind === 'empty' && (note.pending ? t('companion.voice.heardNoQuestion') : t('companion.voice.nothingHeard'))}
            {note.kind === 'unanswered' && t('companion.voice.notPassedOn')}
            {note.kind === 'error' && (note.text || t('companion.voice.couldNotCheck'))}
          </span>
          {canSend && <button type="button" className="cmp-link" onClick={sendPending}><Send size={12} /> {t('companion.voice.sendHeard')}</button>}
        </div>
      )}
      {ackMissing && live && <div className="cmp-asof">{t('companion.voice.contextUnconfirmed')}</div>}
      {state === 'error' && reason && <div className="cmp-note cmp-note-error">{reason}</div>}
      {state === 'closed' && (reason || usage) && <div className="cmp-asof">{reason ? reason : t('companion.voice.usageConfirmed', { seconds: usage?.duration_seconds ?? usage?.seconds ?? '' })}</div>}
    </div>
  );
}
